/**
 * Exhaustiveness guard for semantic-variant matching.
 *
 * WHY THIS EXISTS
 *   FE-1 Stage Work Item §7.2 requires the semantic separations to be
 *   non-collapsible. The main way a union collapses in practice is a `default:`
 *   branch that quietly absorbs a variant it was never meant to handle — the
 *   classic shape being a loading state or an unknown state falling into a
 *   generic "not ready" bucket.
 *
 *   Matching a semantic union must therefore be a total `switch` with no
 *   `default`, ending in `assertNever`. Adding a variant then becomes a COMPILE
 *   error at every match site rather than a silent runtime fallthrough.
 *
 * WHAT IT IS NOT
 *   This is a frontend DEFECT path, not a read-model state. It throws; it never
 *   renders, and it must never be caught and converted into `UNKNOWN`,
 *   `UNAVAILABLE`, empty or a default. Reaching it means the type system was
 *   bypassed, which is an engineering defect (the `ERROR` condition), and it is
 *   reported as one.
 */
export function assertNever(value: never, context: string): never {
  throw new Error(
    `FE1-W0 exhaustive-match violation (${context}): an unhandled semantic variant ` +
      `reached the terminal branch. This is a frontend engineering defect, not a ` +
      `read-model state. Variant: ${String(value)}`,
  );
}
